import { useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { Typography } from '@shinetools/sunshine-universal';

import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import SunshineCard from 'components/_core/SunshineCard';

import locales from '../locales';

const LA_POSTE_TRACKING_PAGE_URL =
  'https://www.laposte.fr/outils/suivre-vos-envois?code=';

const TrackingInformation = ({
  actualDeliveryAddress,
  deliveryCity,
  deliveryFirstStreetLine,
  deliverySecondStreetLine,
  deliveryZip,
  shippingDate,
  trackingNumber,
}: {
  actualDeliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryZip: string | null;
  deliveryFirstStreetLine: string | null;
  deliverySecondStreetLine: string | null;
  shippingDate: number | null;
  trackingNumber: string | null;
}) => {
  const lang = useCurrentLanguage();

  const formattedDate = useMemo(() => {
    if (shippingDate) {
      const date = new Date(shippingDate);

      const options = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      } as Intl.DateTimeFormatOptions;

      const formatter = new Intl.DateTimeFormat(
        lang === 'fr' ? 'fr-FR' : 'en-US',
        options,
      );
      return formatter.format(date);
    }
    return null;
  }, [shippingDate, lang]);

  const address = useMemo(() => {
    return actualDeliveryAddress
      ? actualDeliveryAddress
      : `${deliveryFirstStreetLine}, ${deliverySecondStreetLine ? deliverySecondStreetLine + ',' : ''} ${deliveryZip}, ${deliveryCity}`;
  }, [
    actualDeliveryAddress,
    deliveryFirstStreetLine,
    deliverySecondStreetLine,
    deliveryZip,
    deliveryCity,
  ]);

  return (
    <SunshineCard isBoxed={true} title={locales.trackingInformation}>
      <SunshineCard.Content gap="space-12" marginTop="space-12">
        {trackingNumber ? (
          <Flex flexDirection="column">
            <SunshineCard.Text color="primary" fontWeight="weight-500">
              {locales.trackingNumber}
            </SunshineCard.Text>
            <Typography.Link
              href={`${LA_POSTE_TRACKING_PAGE_URL}${trackingNumber}`}
              target="_blank"
            >
              {trackingNumber}
            </Typography.Link>
          </Flex>
        ) : null}

        <Flex flexDirection="column">
          <SunshineCard.Text color="primary" fontWeight="weight-500">
            {locales.shippingDateTitle}
          </SunshineCard.Text>
          <SunshineCard.Text>
            {shippingDate ? `${formattedDate}` : locales.shippingNotAvailable}
          </SunshineCard.Text>
        </Flex>

        <Flex flexDirection="column">
          <SunshineCard.Text color="primary" fontWeight="weight-500">
            {locales.deliveryAddress}
          </SunshineCard.Text>
          <SunshineCard.Text>{address}</SunshineCard.Text>
        </Flex>
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default TrackingInformation;
